import { cn } from '~/lib/utils';
import { ImageThumb } from './ImageThumb';
import styles from './ThumbnailStrip.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ThumbnailStripProps {
  /**
   * One CSS colour per tile. The artboard ships three hand-picked stand-ins —
   * `#7C93A8` slate, `#8B8A63` olive, `#5C4A6E` plum — and they are genuinely hardcoded rather
   * than tokens: they represent photographs, and a photograph is not a design-system colour.
   */
  fills: string[];
  theme?: ChatTheme;
  className?: string;
}

/**
 * The body of the vision tool: a row of 76x52 rectangles standing in for the images the agent is
 * looking at.
 *
 * `Image analyze` is the only artboard that draws it, and it is the only card body in the
 * chat with no border, no padding and no radius of its own — the tool's payload is the imagery, so
 * the card shows the imagery and nothing around it. It is also the only place `--radius-md` turns
 * up in a body; every other one is `--radius-xl`. Both of those live on `ImageThumb`, which owns
 * the tile; this component owns only the row.
 *
 * The tiles are placeholders, not content — they carry no alt text because there is nothing yet to
 * describe. The activity row above ("Analyzing 3 images · 3 of 3") is what a screen reader needs
 * and already gets, so the strip is hidden from it rather than announced as three blank images.
 */
export function ThumbnailStrip({ fills, theme: themeProp, className }: ThumbnailStripProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(chatThemeClass(theme), styles.root, className)} aria-hidden="true">
      {fills.map((fill, i) => (
        // Two images in one message can legitimately be the same colour, so the fill is not a key.
        // The strip is static for the life of the card, so the index is the tile's only identity.
        <ImageThumb key={i} size="strip" fill={fill} theme={theme} />
      ))}
    </div>
  );
}
